import styled from "styled-components";
import { RASelect, RATypography, RAUploadFiles } from "@/components/customs";
import { RAImage, RATextInput, RAButtonPrimary } from "@/components/customs";
import { Row, Col, Grid } from "antd";
import { PATH_PUBLIC_IMAGES } from "@/config/images";
import { useTrackVisibility } from "react-intersection-observer-hook";
import { CONTACT_TYPE, CONTACT_TYPE_OPTIONS, NOTIFICATION_TYPE, REGEX_EMAIL } from "@/config/constants";

import React, { useState } from "react";
import { isEmpty } from "lodash";
import { createUserContact, apiUploadPublicFile } from "@/stores/apis/apiSettings";
import { toast } from "@/modules/toast";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { HeaderSection } from "../../Cards";

const { useBreakpoint } = Grid;

const initForm = {
	first_name: "",
	type: CONTACT_TYPE.GENERAL_INQUIRY,
	last_name: "",
	phone: "",
	email: "",
	note: "",
	attachments: [],
};

export const ContactUsGetInTouch = React.forwardRef(({ get_in_touch_with_us = {} }, ref) => {
	const [formContact, setFormContact] = useState(initForm);
	const [loading, setLoading] = useState(false);
	const screens = useBreakpoint();

	const validateFormContact = () => {
		let errs = {};
		if (!formContact.first_name) {
			errs.first_name = "Please enter first name";
		}
		if (!formContact.last_name) {
			errs.last_name = "Please enter last name";
		}
		if (!formContact.type) {
			errs.type = "Please select type";
		}
		if (!formContact.phone) {
			errs.phone = "Please enter your phone";
		} else if (!isPossiblePhoneNumber(formContact.phone)) {
			return toast({
				type: NOTIFICATION_TYPE.WARNING,
				description: "Please enter valid phone number",
			});
		}
		if (!formContact.email) {
			errs.email = "Please enter your email";
		} else if (!REGEX_EMAIL.test(formContact.email)) {
			return toast({
				type: NOTIFICATION_TYPE.WARNING,
				description: "Please enter valid email",
			});
		}
		if (!formContact.note) {
			errs.note = "Please enter special request";
		}

		if (!isEmpty(errs)) {
			toast({
				type: NOTIFICATION_TYPE.WARNING,
				description: "Oops! It looks like some fields are missing. Please complete all required fields.",
			});
			return false;
		}

		return true;
	};

	const handleSubmit = async () => {
		let validForm = validateFormContact();
		if (!validForm || loading) return;

		setLoading(true);

		let payload = {
			first_name: formContact?.first_name,
			type: formContact?.type,
			last_name: formContact?.last_name,
			phone: formContact?.phone,
			email: formContact?.email,
			note: formContact?.note,
			attachments: [],
		};

		// upload attachments before fetch submit contact form
		if (formContact?.attachments?.length > 0) {
			const uploadedFiles = await Promise.allSettled(
				formContact.attachments.map(async (file) => {
					const fileUrl = await apiUploadPublicFile({
						file: file?.originFileObj,
					});
					return fileUrl;
				}),
			);
			payload.attachments = uploadedFiles?.map((v) => v?.value?.data?._id);
		}

		let response = await createUserContact(payload);
		if (response && response.success) {
			toast({
				type: NOTIFICATION_TYPE.SUCCESS,
				description: "Thanks for reaching out! We've got your message and will be in touch soon.",
			});
			setFormContact({ ...initForm });
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Your message failed to send. Please try again later.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 300);
	};

	return (
		<HomeUtilitiesContainer>
			<EachCol className="image-column" xxl={13} xl={13} lg={12} md={24} xs={24}>
				<ImageContainer>
					<EachImage>
						<RAImage
							url={
								screens.xs
									? get_in_touch_with_us?.mobile_banner
									: get_in_touch_with_us?.desktop_banner || PATH_PUBLIC_IMAGES.contact_image_1
							}
							width="100%"
							height="100%"
							borderRadius="12px"
							className="image-left"
						/>
					</EachImage>
				</ImageContainer>
			</EachCol>
			<EachCol className="introduction-column" xxl={11} xl={11} lg={12} md={24} xs={24}>
				<div ref={ref}>
					<WrapperContent>
						<HeaderSection
							title={get_in_touch_with_us?.title || ""}
							description={get_in_touch_with_us?.sub_title || ""}
						/>
					</WrapperContent>
					<WrapperSection>
						<div>
							<Row gutter={[20, 20]}>
								<Col md={12} xs={24}>
									<RATextInput
										title="First name *"
										placeholder=" "
										value={formContact.first_name}
										onChange={(val) => {
											setFormContact({
												...formContact,
												first_name: val,
											});
										}}
									/>
								</Col>
								<Col md={12} xs={24}>
									<RATextInput
										title="Last name *"
										placeholder=" "
										value={formContact.last_name}
										onChange={(val) => {
											setFormContact({
												...formContact,
												last_name: val,
											});
										}}
									/>
								</Col>
							</Row>
						</div>
						<div>
							<Row gutter={[20, 20]} style={{ marginTop: "12px" }}>
								<Col md={12} xs={24}>
									<RATypography color="#595959" variant="sub-heading4" bottom={8}>
										Type
									</RATypography>
									<RASelect
										options={CONTACT_TYPE_OPTIONS}
										value={formContact.type}
										onChange={(val) => {
											setFormContact({
												...formContact,
												type: val,
											});
										}}
									/>
								</Col>
								<Col md={12} xs={24}>
									<RATextInput
										title="Your phone number *"
										placeholder=" "
										value={formContact.phone}
										type="phone"
										onChange={(val) => {
											setFormContact({
												...formContact,
												phone: val,
											});
										}}
									/>
								</Col>
							</Row>
						</div>
						<div>
							<Row gutter={[20, 20]} style={{ marginTop: "12px" }}>
								<Col span={24}>
									<RATextInput
										title="Your email *"
										placeholder=" "
										value={formContact.email}
										onChange={(val) => {
											setFormContact({
												...formContact,
												email: val,
											});
										}}
									/>
								</Col>
							</Row>
						</div>
						<div>
							<Row gutter={[20, 20]} style={{ marginTop: "12px" }}>
								<Col span={24}>
									<RATextInput
										type="textarea"
										title="Message *"
										placeholder=" "
										value={formContact.note}
										minRows={2}
										onChange={(val) => {
											setFormContact({
												...formContact,
												note: val,
											});
										}}
									/>
								</Col>
							</Row>
						</div>
						<div>
							<Row gutter={[20, 20]} style={{ marginTop: "12px" }}>
								<Col span={24}>
									<RAUploadFiles
										title="Upload Documents"
										description="JPG, PNG, PDF (max 10 MB)"
										value={formContact?.attachments}
										onChange={(vals) =>
											setFormContact({
												...formContact,
												attachments: vals,
											})
										}
									/>
								</Col>
							</Row>
						</div>
						<div>
							<Row gutter={[20, 20]} style={{ marginTop: "20px" }}>
								<Col span={24}>
									<RAButtonPrimary
										title="SUBMIT"
										width="180px"
										onClick={handleSubmit}
										loading={loading}
									/>
								</Col>
							</Row>
						</div>
					</WrapperSection>
				</div>
			</EachCol>
		</HomeUtilitiesContainer>
	);
});

export function AvailablePositions({ available_positions = {} }) {
	const [ref] = useTrackVisibility();
	return (
		<Container ref={ref}>
			<EachRow gutter={[{ xxl: 60, xl: 60, lg: 40, md: 0, sm: 0, xs: 0 }, 30]} align="middle">
				<Col
					xxl={{ span: 12, order: 1 }}
					xl={{ span: 12, order: 1 }}
					lg={{ span: 12, order: 1 }}
					md={{ span: 24, order: 2 }}
					sm={{ span: 24, order: 2 }}
					xs={{ span: 24, order: 2 }}
				>
					<WrapperContent>
						<HeaderSection
							title={available_positions?.title || ""}
							description={available_positions?.description || ""}
						/>
					</WrapperContent>
					<div style={{ marginTop: "30px" }}>
						<div className="center">
							<button
								className="btn"
								onClick={() => window.open(available_positions?.button_link || "", "_blank")}
							>
								<svg width="235" height="51.5" viewBox="0 0 235 51.5" className="border">
									<rect x="1" y="0.5" width="233" height="50" rx="25" />
								</svg>
								<span>{available_positions?.button_content || ""}</span>
							</button>
						</div>
					</div>
				</Col>
				<Col
					xxl={{ span: 12, order: 2 }}
					xl={{ span: 12, order: 2 }}
					lg={{ span: 12, order: 2 }}
					md={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					xs={{ span: 24, order: 1 }}
				>
					<Row
						style={{
							width: "100%",
							minHeight: "330px",
							position: "relative",
						}}
					>
						<Col span={24}>
							<RAImage
								url={available_positions?.photo || PATH_PUBLIC_IMAGES.journey_available_positions}
								width="100%"
								height="100%"
								borderRadius="16px"
							/>
						</Col>
					</Row>
				</Col>
			</EachRow>
		</Container>
	);
}

export function JoinOurTeam({ joining_our_team = {} }) {
	const [ref] = useTrackVisibility();
	return (
		<Container ref={ref}>
			<EachRow gutter={[{ xxl: 60, xl: 60, lg: 40, md: 0, sm: 0, xs: 0 }, 30]} align="middle">
				<Col
					xxl={{ span: 12 }}
					xl={{ span: 12 }}
					lg={{ span: 12 }}
					md={{ span: 24 }}
					sm={{ span: 24 }}
					xs={{ span: 24 }}
				>
					<Row
						style={{
							width: "100%",
							minHeight: "330px",
							position: "relative",
						}}
					>
						<Col span={24}>
							<RAImage
								url={joining_our_team?.photo || PATH_PUBLIC_IMAGES.journey_join_team}
								width="100%"
								height="100%"
								borderRadius="16px"
							/>
						</Col>
					</Row>
				</Col>
				<Col
					xxl={{ span: 12 }}
					xl={{ span: 12 }}
					lg={{ span: 12 }}
					md={{ span: 24 }}
					sm={{ span: 24 }}
					xs={{ span: 24 }}
				>
					<WrapperContent>
						<HeaderSection
							title={joining_our_team?.title || ""}
							description={joining_our_team?.description || ""}
						/>
					</WrapperContent>
				</Col>
			</EachRow>
		</Container>
	);
}

const ImageContainer = styled.div`
	width: 100%;
	height: 100%;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 400px 300px;
	gap: 8px;
	@media (max-width: 1199px) {
		grid-template-rows: 360px 240px;
	}
	@media (max-width: 991px) {
		grid-template-rows: 252px 168px;
	}
	@media (max-width: 767px) {
		grid-template-rows: 228px 152px;
	}
	@media (max-width: 575px) {
		grid-template-rows: 202px 136px;
	}
`;
const EachImage = styled.div`
	&:first-child {
		grid-column: 1/8;
		grid-row: 1/3;
	}
	&:nth-child(2) {
		grid-column: 4/6;
		grid-row: 1/2;
	}
	&:nth-child(3) {
		grid-column: 4/6;
		grid-row: 2/3;
	}
`;
const HomeUtilitiesContainer = styled(Row)`
	width: 100%;
	max-width: 1442px;
	.image-column {
		padding-right: 30px;
	}
	.introduction-column {
		padding-left: 30px;
	}
	@media (max-width: 1199px) {
		.image-column {
			padding-right: 20px;
		}
		.introduction-column {
			padding-left: 20px;
		}
	}
	@media (max-width: 991px) {
		.image-column {
			padding-right: 0px;
		}
		.introduction-column {
			padding-left: 0;
		}
	}
	@media (max-width: 575px) {
		.image-column {
			padding-right: 0px;
		}
		.introduction-column {
			padding-left: 0;
		}
	}
`;
const EachCol = styled(Col)`
	display: flex;
	flex-direction: column;
	align-self: center;
	padding-top: 20px;
`;
const WrapperContent = styled.div`
	margin-top: 40px;
`;
const WrapperSection = styled.div`
	padding: 12px 0px;
	display: flex;
	flex-direction: column;
`;
const EachRow = styled(Row)``;
const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 60px;
	.center {
		width: 235px;
		height: 52px;
	}

	.btn {
		width: 235px;
		height: 52px;
		border-radius: 50px;
		cursor: pointer;
		background: transparent;
		border: 1px solid #ffb11a;
		outline: none;
		transition: 2s ease-in-out;
		box-sizing: border-box;
		position: relative;
	}

	svg {
		position: absolute;
		left: -1px;
		top: -1px;
		fill: none;
		stroke: #404040;
		stroke-dasharray: 100 560;
		stroke-dashoffset: 100;
		transition: 2s ease-in-out;
		z-index: 100;
		box-sizing: border-box;
	}

	.btn:hover {
		transition: 2s ease-in-out;
	}

	.btn:hover svg {
		stroke-dashoffset: -560;
	}

	.btn span {
		color: #222222;
		font-family: "DM Sans";
		font-weight: 500;
		font-size: 14px;
		/* line-height: 30px; */
		letter-spacing: 3px;
		text-transform: uppercase;
		text-indent: 3px;
	}
`;
